import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AppRoutes from './routes';
import { Providers } from './app/providers';
import {
  SESSION_EXPIRED_EVENT,
  dismissSessionExpiryPrompt,
  clearSessionAndRedirectToLogin,
  continueSession,
  emitSessionContinued,
} from './services/auth/sessionExpiry';
import './App.css';

function App() {
  const [sessionExpiredModal, setSessionExpiredModal] = useState({ open: false, reason: '' });
  const [continuingSession, setContinuingSession] = useState(false);
  const [sessionExpiryCountdown, setSessionExpiryCountdown] = useState(60);
  const countdownTimerRef = useRef(null);

  useEffect(() => {
    // Initial load
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      setSessionExpiredModal({
        open: true,
        reason: event?.detail?.reason || 'Your session has expired.',
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  useEffect(() => {
    if (!sessionExpiredModal.open) return undefined;

    setSessionExpiryCountdown(60);
    countdownTimerRef.current = setInterval(() => {
      setSessionExpiryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          setSessionExpiredModal({ open: false, reason: '' });
          clearSessionAndRedirectToLogin();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    };
  }, [sessionExpiredModal.open]);

  const handleContinueSession = async () => {
    clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = null;
    setContinuingSession(true);
    const ok = await continueSession();
    setContinuingSession(false);

    if (ok) {
      emitSessionContinued();
      dismissSessionExpiryPrompt();
      setSessionExpiredModal({ open: false, reason: '' });
      return;
    }

    clearSessionAndRedirectToLogin();
  };

  return (
    <Providers>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      {sessionExpiredModal.open && (
        <div className="session-expiry-overlay" role="presentation">
          <div className="session-expiry-modal" role="dialog" aria-modal="true" aria-labelledby="session-expiry-title">
            <h3 id="session-expiry-title">Session expired</h3>
            <p>{sessionExpiredModal.reason}</p>
            <p>Redirecting to login in {sessionExpiryCountdown}s if no action is taken.</p>
            <div className="session-expiry-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={handleContinueSession} disabled={continuingSession}>
                {continuingSession ? 'Restoring session...' : 'Continue current session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Providers>
  );
}

export default App;

